import React, { useMemo, useState } from 'react';
import { Dropdown, Form } from 'react-bootstrap';
import moment from 'moment-timezone';

/** The IANA list never changes at runtime, so it is built once at module load. */
const TIME_ZONES: readonly string[] = moment.tz.names();

/**
 * How many zones the menu renders at once. There are ~600, and a menu holding
 * all of them is a lot of DOM for a control most operators open to pick one
 * known zone. The filter is the way through, and the footer says plainly how
 * many were left out rather than letting the list look complete.
 */
const VISIBLE_LIMIT = 50;

interface PreviewTimezonePickerProps {
  /** The zone explicitly chosen, or null while following the schedule. */
  readonly value: string | null;
  /** The zone actually in use — what the times on screen are rendered in. */
  readonly resolved: string;
  readonly onChange: (timezone: string | null) => void;
}

/**
 * Chooses the timezone the schedule preview is read in.
 *
 * Deliberately not a form control. This changes nothing that gets submitted and
 * nothing the backend computes — it only re-reads already-decided instants from
 * another angle — and the submit timezone select sits a few lines below on the
 * same form. A bordered field here would read as a second scheduling input.
 *
 * So the trigger is the zone's own name as text, and it always shows the
 * RESOLVED zone rather than the selection: that is what the times on screen are
 * in, it names the full IANA zone the per-line abbreviation often cannot (`z`
 * yields "GMT" for London and bare offsets like "+07" elsewhere), and it makes a
 * chosen zone that no longer matches the form self-evident instead of hidden.
 *
 * The list is the full IANA set, not the plausible candidates — any zone is a
 * legitimate angle to want. Only the initial value is resolved for the operator.
 */
const PreviewTimezonePicker: React.FC<PreviewTimezonePickerProps> = ({ value, resolved, onChange }) => {
  const [show, setShow] = useState(false);
  const [filter, setFilter] = useState('');

  const matches = useMemo(() => {
    const needle = filter.trim().toLowerCase();
    return needle ? TIME_ZONES.filter((zone) => zone.toLowerCase().includes(needle)) : TIME_ZONES;
  }, [filter]);

  const visible = matches.slice(0, VISIBLE_LIMIT);

  // Opening always starts from the unfiltered list: a filter left over from the
  // last visit would look like the list is all there is.
  const toggle = (next: boolean): void => {
    setShow(next);
    if (next) {
      setFilter('');
    }
  };

  const pick = (timezone: string | null): void => {
    onChange(timezone);
    setShow(false);
  };

  return (
    <div className="text-muted small mt-2">
      Times shown in{' '}
      {/* `autoClose="outside"` and a controlled `show`: the filter box lives
          inside the menu, and the default auto-close treats a click on it as a
          dismissal. Selection closes the menu explicitly instead. */}
      <Dropdown show={show} onToggle={toggle} autoClose="outside" className="d-inline-block">
        <Dropdown.Toggle variant="link" size="sm" className="p-0 align-baseline text-decoration-none lh-1">
          {resolved}
        </Dropdown.Toggle>
        <Dropdown.Menu className="py-1" style={{ maxHeight: '18rem', overflowY: 'auto', minWidth: '16rem' }}>
          <div className="px-2 pb-1">
            <Form.Control
              size="sm"
              type="search"
              autoFocus
              value={filter}
              onChange={(event) => setFilter(event.currentTarget.value)}
              placeholder="Filter timezones"
              aria-label="Filter timezones"
            />
          </div>
          <Dropdown.Item active={value === null} onClick={() => pick(null)}>
            Follow the schedule
          </Dropdown.Item>
          <Dropdown.Divider className="my-1" />
          {visible.map((zone) => (
            <Dropdown.Item key={zone} active={value === zone} onClick={() => pick(zone)}>
              {zone}
            </Dropdown.Item>
          ))}
          {visible.length === 0 && <div className="px-3 py-1 text-muted fst-italic small">No matching timezone</div>}
          {matches.length > visible.length && (
            <div className="px-3 pt-1 text-muted small">
              Showing {visible.length} of {matches.length} — keep typing to narrow
            </div>
          )}
        </Dropdown.Menu>
      </Dropdown>
    </div>
  );
};

export default PreviewTimezonePicker;
